import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { setAuthInterceptors } from '@lib/axios';
import { useBusinessSettingsStore } from '@features/onboarding/store/businessSettingsStore';

import type { AccountInfo, AuthState, AuthTokens, User } from '../types/auth.types';

interface AuthActions {
  setUser: (user: User) => void;
  setTokens: (tokens: AuthTokens) => void;
  setActiveAccount: (account: AccountInfo) => void;
  logout: () => void;
  setLoading: (isLoading: boolean) => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      activeAccount: null,

      setUser: (user) => {
        set({ user, isAuthenticated: true });
      },
      setTokens: (tokens) => {
        set({ tokens });
        setAuthInterceptors(
          () => get().tokens?.idToken ?? null,
          () => get().logout(),
        );
      },
      setActiveAccount: (account) => {
        set({ activeAccount: account });
      },
      logout: () => {
        set({ user: null, tokens: null, isAuthenticated: false, activeAccount: null });
        setAuthInterceptors(
          () => null,
          () => undefined,
        );
        useBusinessSettingsStore.getState().reset();
      },
      setLoading: (isLoading) => {
        set({ isLoading });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
        activeAccount: state.activeAccount,
      }),
      onRehydrateStorage: () => (state) => {
        // Re-init axios interceptors so the auth header is sent after a page reload.
        if (state?.tokens) {
          setAuthInterceptors(
            () => state.tokens?.idToken ?? null,
            () => state.logout(),
          );
        }
      },
    },
  ),
);

// Selectors
export const selectUser = (state: AuthStore): User | null => state.user;
export const selectIsAuthenticated = (state: AuthStore): boolean => state.isAuthenticated;
export const selectIsLoading = (state: AuthStore): boolean => state.isLoading;
export const selectTokens = (state: AuthStore): AuthTokens | null => state.tokens;
export const selectActiveAccount = (state: AuthStore): AccountInfo | null => state.activeAccount;
